import { useMemo } from "react";
import { ContractLight, ContractStatus } from "@/types";
import {
  getContractDisplayName,
  getContractProgressPercent,
  getContractTradeType,
  getTradingPartnerCompany,
} from "@/utils/Contracts";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mantine/core";
import { useGetCreateOrderValues } from "@/services/Orders";
import { prefetchContractById } from "@/services/Contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useDebouncedPrefetch } from "@/hooks/useDebouncedPrefetch";
import { ColumnDef } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { useOpenCreateOrder } from "@/hooks/useGuardedWizards";
import { useContractStore } from "@/stores/contractStore";
import { useContractBulkEditStore } from "@/stores/contractBulkEditStore";
import { useAuth } from "@/contexts/AuthContext";
import DataTable from "@/components/molecules/DataTable";
import CTA from "@/components/atoms/CTA";
import Checkbox from "@/components/atoms/Checkbox";
import TradeIdentifier from "@/components/atoms/TradeIdentifier";
import Text from "@/components/atoms/Text";
import dayjs from "dayjs";
import ContractStatusEnum from "@/constants/enums/ContractStatusEnum";

import styles from "./styles.module.css";

interface ContractListProps {
  contracts: ContractLight[];
  isLoading: boolean;
  totalItems: number;
  onClickApproveContract: (contract: ContractLight) => void;
  isPendingApproval?: boolean;
}

// comment to trigger branch build
export default function ContractList({
  contracts,
  isLoading,
  totalItems,
  onClickApproveContract,
  isPendingApproval,
}: ContractListProps) {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const editMode = useContractBulkEditStore((state) => state.editMode);
  const selectedRows = useContractBulkEditStore((state) => state.selectedRows);
  const toggleRow = useContractBulkEditStore((state) => state.toggleRow);
  const contractType = useContractBulkEditStore((state) => state.contractType);
  const setContractType = useContractBulkEditStore((state) => state.setContractType);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const schedulePrefetch = useDebouncedPrefetch();
  const open = useOpenCreateOrder();
  const limit = useContractStore((state) => state.limit);
  const setLimit = useContractStore((state) => state.setLimit);
  const currentPage = useContractStore((state) => state.currentPage);
  const setCurrentPage = useContractStore((state) => state.setPage);

  const { data: createOrderValues } = useGetCreateOrderValues();

  const baseColumns: ColumnDef<ContractLight>[] = useMemo(
    () => [
      {
        id: "contract",
        header: () => t("common:contract"),
        cell: ({ row }) => {
          const tradeType = getContractTradeType(row.original, currentCompany);
          const partner = getTradingPartnerCompany(row.original, currentCompany?.companyId);
          return (
            <TradeIdentifier
              tradeType={tradeType}
              id={row.original.id}
              companyName={partner?.companyName}
              contractName={getContractDisplayName(row.original)}
            />
          );
        },
      },
      {
        id: "type",
        header: () => t("common:type"),
        size: 250,
        cell: ({ row }) => {
          const productName =
            createOrderValues?.salesItems.find(
              (m) => m.id === row.original.contractProduct.companySpecificProduct.salesItemId
            )?.value ?? row.original.contractProduct.companySpecificProduct.companySpecificProductName;
          return (
            <Text display="technical" size="sm">
              {productName}
            </Text>
          );
        },
      },
      {
        id: "progress",
        header: () => t("common:progress"),
        size: 170,
        cell: ({ row }) => {
          const pct = Math.round(getContractProgressPercent(row.original));
          const isArchived = row.original.contractStatus === ContractStatusEnum.ARCHIVED;
          return <InlineProgress pct={pct} isArchived={isArchived} />;
        },
      },
      {
        id: "created",
        header: () => t("common:created"),
        size: 150,
        cell: ({ row }) => (
          <Text size="md" color="tertiary">
            {dayjs(row.original.created).format("DD.MM.YYYY")}
          </Text>
        ),
      },
      {
        id: "action",
        header: () => <></>,
        size: 170,
        cell: ({ row }) => (
          <RowAction
            contract={row.original}
            status={row.original.contractStatus}
            currentCompanyId={currentCompany?.companyId}
            onClickNewOrder={() => open(row.original, "contractLight")}
            onClickApproveContract={() => onClickApproveContract(row.original)}
            isPendingApproval={isPendingApproval}
          />
        ),
      },
    ],
    [t, currentCompany, createOrderValues, onClickApproveContract, open, isPendingApproval]
  );

  const columns: ColumnDef<ContractLight>[] = useMemo(() => {
    if (!editMode) return baseColumns;

    const selectColumn: ColumnDef<ContractLight> = {
      id: "select",
      header: () => <></>,
      size: 48,
      cell: ({ row }) => {
        const disabled = shouldRowBeDisabled(row.original, contractType);
        const checked = selectedRows.some((selected) => selected.id === row.original.id);
        return (
          <Checkbox
            aria-label={t("contracts:selectContract")}
            disabled={disabled}
            checked={checked}
            onClick={(e) => e.stopPropagation()}
            onChange={(event) => {
              if (selectedRows.length === 0) {
                setContractType(row.original.contractProduct.priceUnit.unit);
              }
              toggleRow(row.original, event.currentTarget.checked);
            }}
          />
        );
      },
    };

    return [selectColumn, ...baseColumns];
  }, [baseColumns, editMode, selectedRows, contractType, toggleRow, setContractType, t]);

  return (
    <DataTable
      data={contracts}
      columns={columns}
      isLoading={isLoading}
      rowsPerPage={limit}
      totalItems={totalItems}
      activePage={currentPage}
      onPageChange={setCurrentPage}
      onRowsPerPageChange={(rows) => setLimit(rows)}
      useServerSidePagination
      testIdPrefix="contracts"
      mobileHeaderCell={editMode ? 1 : 0}
      onRowClick={(row) => navigate(`/contracts/details/${row.original.id}`)}
      onRowMouseEnter={(row) => schedulePrefetch(() => prefetchContractById(queryClient, row.original.id))}
      skipPrefetchForColumn={editMode ? "select" : "action"}
      rowClassName={(row) => (row.contractStatus === ContractStatusEnum.ARCHIVED ? styles.archivedRow : "")}
    />
  );
}

interface InlineProgressProps {
  pct: number;
  isArchived: boolean;
}

const InlineProgress = ({ pct, isArchived }: InlineProgressProps) => (
  <div className={styles.progressCell}>
    <div className={styles.progressTrack}>
      <div
        className={styles.progressFill}
        style={{
          width: `${pct}%`,
          backgroundColor: isArchived ? "var(--color-grey-50)" : "var(--color-black)",
        }}
      />
    </div>
    <Text display="technical" className={styles.progressPct}>
      {pct}%
    </Text>
  </div>
);

interface RowActionProps {
  contract: ContractLight;
  status: ContractStatus;
  currentCompanyId: number | undefined;
  onClickNewOrder: () => void;
  onClickApproveContract: () => void;
  isPendingApproval?: boolean;
}

const RowAction = ({
  contract,
  status,
  currentCompanyId,
  onClickNewOrder,
  onClickApproveContract,
  isPendingApproval,
}: RowActionProps) => {
  const { t } = useTranslation();
  const stopAndCall = (e: React.SyntheticEvent, fn: () => void) => {
    e.stopPropagation();
    fn();
  };
  if (status === ContractStatusEnum.ACTIVE) {
    return (
      <CTA
        data-testid={`new-order-badge-${contract.id}`}
        intent="primary"
        size="sm"
        onClick={(e) => stopAndCall(e, onClickNewOrder)}
      >
        {t("orders:newOrder")}
      </CTA>
    );
  }

  if (status === ContractStatusEnum.PENDING_APPROVAL) {
    const alreadyApproved = contract.approvals.some((a) => a.approvalCompany === currentCompanyId);
    if (alreadyApproved) {
      return (
        <Tooltip label={t("contracts:counterpartyMustApprove")}>
          <span>
            <CTA intent="secondary" size="sm" disabled>
              {t("actions:approve")}
            </CTA>
          </span>
        </Tooltip>
      );
    }
    return (
      <CTA
        intent="affirm"
        size="sm"
        className={styles.approveBtn}
        loading={isPendingApproval}
        loaderProps={{ color: "#2a2a2a" }}
        onClick={(e) => stopAndCall(e, onClickApproveContract)}
      >
        {t("actions:approve")}
      </CTA>
    );
  }

  if (status === ContractStatusEnum.COMPLETED) {
    return (
      <CTA intent="text" size="sm" style={{ color: "var(--color-completed-primary)", cursor: "default" }}>
        ✓ {t(`enumerables:contractStatus.${status}`)}
      </CTA>
    );
  }

  if (status === ContractStatusEnum.ARCHIVED) {
    return (
      <CTA intent="text" size="sm" style={{ color: "var(--color-grey-50)", cursor: "default" }}>
        {t(`enumerables:contractStatus.${status}`)}
      </CTA>
    );
  }

  return (
    <CTA intent="secondary" size="sm" disabled>
      {t(`enumerables:contractStatus.${status}`)}
    </CTA>
  );
};

const shouldRowBeDisabled = (contract: ContractLight, contractType: string | null): boolean => {
  if (!contractType) {
    return false;
  }
  return contract.contractProduct.priceUnit.unit !== contractType;
};
